import { EditableField, LoadStatus, MoveDirection, Notice, StatItem } from '@/Models'
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { editorReducer, initialEditorState } from './statsEditorReducer'
import { fetchStats, publishStats } from '../../../../../Services/statsImpactService'
import { isStatValid } from '../../../../../Helpers/statsValidation/statsValidation'
import Swal from 'sweetalert2'

const NOTICE_TIMEOUT_MS = 40000

export interface StatsEditor {
  items: StatItem[]
  editingId: string | null
  validatedIds: string[]
  status: LoadStatus
  isDirty: boolean
  isPublishing: boolean
  visibleCount: number
  notice: Notice | null
  /** Polite screen-reader message for reorder / visibility changes. */
  announcement: string
  startEdit: (id: string) => void
  finishEdit: () => void
  changeField: (id: string, field: EditableField, value: string) => void
  blurField: (id: string) => void
  moveItem: (id: string, direction: MoveDirection) => void
  toggleVisibility: (id: string) => void
  discard: () => void
  publish: () => Promise<void>
  dismissNotice: () => void
  reload: () => Promise<void>
}

export function useStatsEditor(): StatsEditor {
  const [state, dispatch] = useReducer(editorReducer, initialEditorState)
  const [status, setStatus] = useState<LoadStatus>('loading')
  const [isPublishing, setIsPublishing] = useState(false)
  const [notice, setNotice] = useState<Notice | null>(null)
  const [announcement, setAnnouncement] = useState('')
  const isMounted = useRef(true)

  // console.log('state', state)

  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
    }
  }, [])

  const reload = useCallback(async () => {
    setStatus('loading')
    try {
      const items = await fetchStats()
      if (!isMounted.current) return
      dispatch({ type: 'loaded', items })
      setStatus('ready')
    } catch {
      if (isMounted.current) setStatus('error')
    }
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  useEffect(() => {
    if (!notice) return undefined
    const timer = window.setTimeout(() => setNotice(null), NOTICE_TIMEOUT_MS)
    return () => window.clearTimeout(timer)
  }, [notice])

  const isDirty = useMemo(
    () => JSON.stringify(state.draft) !== JSON.stringify(state.published),
    [state.draft, state.published],
  )

  const visibleCount = useMemo(
    () => state.draft.filter((item) => item.visible).length,
    [state.draft],
  )

  // Warn before a refresh / tab close would throw away unpublished edits.
  useEffect(() => {
    if (!isDirty) return undefined
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault()
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [isDirty])

  const startEdit = useCallback((id: string) => dispatch({ type: 'edit_started', id }), [])
  const finishEdit = useCallback(() => dispatch({ type: 'edit_finished' }), [])

  const changeField = useCallback(
    (id: string, field: EditableField, value: string) =>
      dispatch({ type: 'field_changed', id, field, value }),
    [],
  )
  const blurField = useCallback((id: string) => dispatch({ type: 'field_blurred', id }), [])

  const moveItem = useCallback(
    (id: string, direction: MoveDirection) => {
      const index = state.draft.findIndex((item) => item.id === id)
      const target = direction === 'up' ? index - 1 : index + 1
      const item = state.draft[index]
      if (!item || target < 0 || target >= state.draft.length) return

      dispatch({ type: 'moved', id, direction })
      setAnnouncement(`${item.label} moved to position ${target + 1} of ${state.draft.length}.`)
    },
    [state.draft],
  )

  const toggleVisibility = useCallback(
    (id: string) => {
      const item = state.draft.find((entry) => entry.id === id)
      if (!item) return
      dispatch({ type: 'visibility_toggled', id })
      setAnnouncement(
        `${item.label} is now ${item.visible ? 'hidden from' : 'visible on'} the landing page.`,
      )
    },
    [state.draft],
  )

  const discard = useCallback(() => {
    dispatch({ type: 'discarded' })
    void Swal.fire({
      position: 'center',
      icon: 'success',
      text: 'Unpublished changes were discarded.',
      showConfirmButton: false,
      timer: 3000,
    })
  }, [])

  const publish = useCallback(async () => {
    if (!state.draft.every(isStatValid)) {
      dispatch({ type: 'publish_blocked' })
      void Swal.fire({
        position: 'center',
        icon: 'warning',
        text: 'Fix the highlighted fields before publishing.',
        showConfirmButton: false,
        timer: 4000,
      })
      return
    }
    if (!state.draft.some((item) => item.visible)) {
      void Swal.fire({
        position: 'center',
        icon: 'warning',
        text: 'At least one stat must be visible.',
        showConfirmButton: false,
        timer: 4000,
      })
      return
    }

    const result = await Swal.fire({
      title: 'Publish changes?',
      text: 'This will update what visitors see on the landing page. Are you sure you want to continue?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#186AFF',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, publish!',
    })
    if (!result.isConfirmed) return

    // Publish changes
    setIsPublishing(true)
    try {
      const saved = await publishStats(state.draft)
      if (!isMounted.current) return
      dispatch({ type: 'published', items: saved })

      void Swal.fire('Done!', 'Changes have been published to the landing page.', 'success')
    } catch (error) {
      const message =
        (error as { response?: { data?: { error?: string } } }).response?.data?.error ??
        'Publishing failed. Your edits are kept, so please try again.'
      void Swal.fire({
        position: 'top-end',
        icon: 'warning',
        text: message,
        showConfirmButton: false,
        timer: 5000,
      })
    } finally {
      if (isMounted.current) setIsPublishing(false)
    }
  }, [state.draft])

  const dismissNotice = useCallback(() => setNotice(null), [])

  return {
    items: state.draft,
    editingId: state.editingId,
    validatedIds: state.validatedIds,
    status,
    isDirty,
    isPublishing,
    visibleCount,
    notice,
    announcement,
    startEdit,
    finishEdit,
    changeField,
    blurField,
    moveItem,
    toggleVisibility,
    discard,
    publish,
    dismissNotice,
    reload,
  }
}
